/**
 * Lightweight in-memory ring buffer + DOM-visible signal for referral capture.
 * Mirrors `[referral]` console.log breadcrumbs so we can debug without DevTools.
 */
export interface ReferralEvent {
  ts: number;
  label: string;
  data?: unknown;
}

const MAX_EVENTS = 30;

declare global {
  interface Window {
    __referralDebug?: ReferralEvent[];
    __referralDebugListeners?: Array<() => void>;
  }
}

function ensure(): ReferralEvent[] {
  if (typeof window === "undefined") return [];
  if (!window.__referralDebug) window.__referralDebug = [];
  if (!window.__referralDebugListeners) window.__referralDebugListeners = [];
  return window.__referralDebug;
}

export function pushReferralEvent(label: string, data?: unknown): void {
  try {
    const buf = ensure();
    buf.push({ ts: Date.now(), label, data });
    if (buf.length > MAX_EVENTS) buf.splice(0, buf.length - MAX_EVENTS);
    window.__referralDebugListeners?.forEach((fn) => {
      try { fn(); } catch { /* ignore */ }
    });
  } catch {
    // ignore
  }
}

export function getReferralEvents(): ReferralEvent[] {
  return ensure().slice();
}

export function subscribeReferralEvents(fn: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  ensure();
  window.__referralDebugListeners!.push(fn);
  return () => {
    window.__referralDebugListeners = (window.__referralDebugListeners || []).filter(
      (l) => l !== fn,
    );
  };
}
